import { useCallback } from "react";

import type { SubOptionItemInput } from "@/lib/gql/__generated__/graphql";
import { CurrencyType } from "@/lib/gql/__generated__/graphql";
import { getClampUUID } from "@/lib/uuid/get-uuid";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { useProductOptionFormModelContext } from "../../../../../context";

// type SubOptionsType = ProductOptionFormModel["ViewModel"]["subOptions"];
type SubOptionItemType = ForAutoGenerator<SubOptionItemInput, "">;

const DEFAULT_SUB_OPTION: (uuid: string) => SubOptionItemType = (uuid: string) => {
	return {
		subOptionID: {
			isEdited: true,
			value: uuid,
		},
		name: {
			isEdited: true,
			value: "",
		},
		price: {
			amount: {
				isEdited: true,
				value: 0,
			},
			currencyType: {
				isEdited: true,
				value: CurrencyType.Jpy,
			},
		},
		maxDailyStockNum: {
			isEdited: true,
			value: 1,
		},
		isNextMorning: {
			isEdited: true,
			value: false,
		},
		isShowTimeSchedule: {
			isEdited: true,
			value: false,
		},
		startAt: {
			isEdited: true,
			value: "12:00",
		},
		endAt: {
			isEdited: true,
			value: "13:00",
		},
	} satisfies SubOptionItemType;
};

export const useSubOptions = () => {
	const { onSetViewModel, viewModel } = useProductOptionFormModelContext();
	const { subOptions } = viewModel;

	const isEdited = subOptions?.isEdited ?? false;
	const hasError = subOptions?.hasError ?? false;

	const appendSubOption = useCallback(() => {
		let uuid = getClampUUID(7);
		const isExist = subOptions?.value?.some((subOption) => subOption.subOptionID?.value === uuid);
		if (isExist) {
			uuid = getClampUUID(7);
		}
		const newSubOption = DEFAULT_SUB_OPTION(uuid);
		const newSubOptions = subOptions?.value?.length ? [...subOptions.value, newSubOption] : [newSubOption];

		onSetViewModel([
			{
				key: "subOptions",
				value: {
					isEdited: true,
					hasError: false,
					value: newSubOptions,
				},
			},
		]);
	}, [subOptions?.value, onSetViewModel]);

	const removeSubOption = useCallback(
		(index: number) => {
			if (
				!window.confirm(
					"今まで、このサブオプションで管理されていた在庫が管理されなくなります。本当に削除しますか？",
				)
			) {
				return;
			}

			const newSubOptions = subOptions?.value?.filter((_, i) => i !== index);

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[subOptions?.value, onSetViewModel],
	);

	const onSetSubOptionName = useCallback(
		(name: string, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						name: {
							isEdited: true,
							value: name,
						},
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[onSetViewModel, subOptions?.value],
	);

	const onSetMaxDailyStockNum = useCallback(
		(maxDailyStockNum: number | undefined, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						maxDailyStockNum: {
							isEdited: true,
							value: maxDailyStockNum ?? 0,
						},
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[onSetViewModel, subOptions?.value],
	);

	const onSetPriceAmount = useCallback(
		(priceAmount: number | undefined, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						price: {
							...subOption.price,
							amount: {
								isEdited: true,
								value: priceAmount ?? 0,
							},
						},
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[onSetViewModel, subOptions?.value],
	);

	const onSetIsNextMorning = useCallback(
		(isNextMorning: boolean, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						isNextMorning: {
							isEdited: true,
							value: isNextMorning,
						},
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[onSetViewModel, subOptions?.value],
	);

	/**
	 * 時間を表示しない設定の場合は、startAtとendAtへのバリデーションは不要なので同じタイミングで更新する
	 * （バリデーションエラーが存在する場合にバリデーションエラーを削除するため）
	 */
	const onSetIsShowTimeSchedule = useCallback(
		(props: { isShowTimeSchedule: boolean; index: number; startAt: string; endAt: string }) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === props.index) {
					return {
						...subOption,
						isShowTimeSchedule: {
							isEdited: true,
							value: props.isShowTimeSchedule,
						},
						startAt: {
							isEdited: true,
							value: props.startAt,
						},
						endAt: {
							isEdited: true,
							value: props.endAt,
						},
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[onSetViewModel, subOptions?.value],
	);

	const isFull = useCallback((startAtOrEndAt: string) => startAtOrEndAt.length === 5, []);
	/**
	 * endAtがstartAtよりも後の時間かどうかを判定する
	 */
	const isEndAtAfterStartAt = useCallback((startAt: string, endAt: string) => startAt <= endAt, []);

	const onSetStartAt = useCallback(
		(startAt: string, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						startAt: {
							isEdited: true,
							value: startAt,
						},
						endAt:
							subOption.endAt?.value &&
							isFull(subOption.endAt.value) &&
							isEndAtAfterStartAt(startAt, subOption.endAt.value)
								? subOption.endAt
								: { isEdited: true, value: startAt },
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[isEndAtAfterStartAt, isFull, onSetViewModel, subOptions?.value],
	);

	const onSetEndAt = useCallback(
		(endAt: string, index: number) => {
			const newSubOptions = subOptions?.value?.map((subOption, i) => {
				if (i === index) {
					return {
						...subOption,
						endAt: {
							isEdited: true,
							value: endAt,
						},
						startAt:
							subOption.startAt?.value &&
							isFull(subOption.startAt.value) &&
							isEndAtAfterStartAt(subOption.startAt.value, endAt)
								? subOption.startAt
								: { isEdited: true, value: endAt },
					};
				}
				return subOption;
			});

			onSetViewModel([
				{
					key: "subOptions",
					value: {
						isEdited: true,
						hasError: false,
						value: newSubOptions,
					},
				},
			]);
		},
		[isEndAtAfterStartAt, isFull, onSetViewModel, subOptions?.value],
	);

	const onUpdateOrderSubOptions = useCallback(
		(subOptionIDs: string[]) => {
			const newSubOptions = subOptionIDs
				.map((id) => subOptions?.value?.find((subOption) => subOption.subOptionID?.value === id))
				.filter((subOption) => subOption !== undefined);

			onSetViewModel([
				{
					key: "subOptions",
					value: { isEdited: true, hasError: false, value: newSubOptions },
				},
			]);
		},
		[subOptions?.value, onSetViewModel],
	);

	return {
		isEdited,
		hasError,

		subOptions,
		appendSubOption,
		removeSubOption,

		onSetSubOptionName,
		onSetPriceAmount,
		onSetMaxDailyStockNum,
		onSetIsNextMorning,
		onSetIsShowTimeSchedule,
		onSetStartAt,
		onSetEndAt,

		onUpdateOrderSubOptions,
	};
};
